"use client";

import { Button } from "@/components/atoms/Button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/molecules/Accordion";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import { ArrowDown, ArrowUpRight, ChevronRight } from "lucide-react";
import Image from "next/image";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";

export interface ProgramStructure3ModuleRow {
  id?: string;
  module_title?: string;
  topics_html?: string;
  priority?: number;
  status?: boolean;
}

export interface ProgramStructure3Payload {
  status?: boolean;
  title?: string;
  section_background_image?: string;
  section_background_image_alt?: string;
  section_overlay_rgba?: string;
  headline_primary?: string;
  headline_primary_color?: string;
  headline_accent?: string;
  headline_accent_color?: string;
  topics_column_title?: string;
  topics_inner_heading?: string;
  card_background_color?: string;
  module_list_track_background_color?: string;
  content_panel_background_color?: string;
  module_inactive_border_color?: string;
  module_list_divider_color?: string;
  module_active_background_color?: string;
  module_active_text_color?: string;
  module_inactive_text_color?: string;
  section_background_color?: string;
  cta_button_text?: string;
  cta_button_text_color?: string;
  cta_button_color?: string;
  /** Outline CTA (e.g. Download Brochure) — after primary CTA */
  brochure_cta_text?: string;
  brochure_cta_text_color?: string;
  brochure_cta_border_color?: string;
  cta_button_is_cta?: boolean;
  brochure_cta_is_cta?: boolean;
  metadata?: ProgramStructure3ModuleRow[];
}

interface ProgramStructure3Props {
  data: ProgramStructure3Payload | { data: ProgramStructure3Payload };
}

function resolvePayload(
  raw: ProgramStructure3Props["data"],
): ProgramStructure3Payload {
  const anyData = raw as { data?: ProgramStructure3Payload };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data;
  }
  return (raw as ProgramStructure3Payload) || {};
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function TopicsBody({
  topicsColumnTitle,
  topicsInnerHeading,
  panelBg,
  html,
  topicDividerColor = "#E5E7EB",
}: {
  topicsColumnTitle: string;
  topicsInnerHeading: string;
  panelBg: string;
  html: string;
  topicDividerColor?: string;
}) {
  const ruleStyle = {
    "--ps3-topic-rule": topicDividerColor,
  } as CSSProperties;

  return (
    <div className="flex flex-col gap-3 w-full">
      <p className="min-[1024px]:text-lg text-base font-semibold text-gray-900 leading-snug pt-2 min-[1024px]:pt-0 px-1 min-[1024px]:px-0">
        {topicsColumnTitle}
      </p>
      <div
        className="flex flex-col gap-3 p-4 min-[1024px]:p-[18px] rounded-2xl"
        style={{ backgroundColor: panelBg }}
      >
        <div className="flex justify-between gap-4">
          <p className="min-[1024px]:text-lg text-base font-semibold text-gray-900 leading-snug">
            {topicsInnerHeading}
          </p>
        </div>
        <div className="max-h-[264px] min-[1024px]:max-h-[300px] overflow-y-auto custom-scrollbar pr-1">
          <div
            className={cn(
              "text-sm min-[1024px]:text-[14px] min-[1024px]:leading-[22px] text-gray-800",
              "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
              "[&_p:not(:last-child)]:border-b [&_p:not(:last-child)]:border-solid [&_p:not(:last-child)]:border-[color:var(--ps3-topic-rule)] [&_p:not(:last-child)]:pb-3 [&_p:not(:last-child)]:mb-2",
              "[&_li:not(:last-child)]:border-b [&_li:not(:last-child)]:border-solid [&_li:not(:last-child)]:border-[color:var(--ps3-topic-rule)] [&_li:not(:last-child)]:pb-2 [&_li:not(:last-child)]:mb-2",
              "[&>div:not(:last-child)]:border-b [&>div:not(:last-child)]:border-solid [&>div:not(:last-child)]:border-[color:var(--ps3-topic-rule)] [&>div:not(:last-child)]:pb-3 [&>div:not(:last-child)]:mb-2",
            )}
            style={ruleStyle}
            dangerouslySetInnerHTML={{ __html: html || "" }}
          />
        </div>
      </div>
    </div>
  );
}

const ProgramStructure3: FC<ProgramStructure3Props & { className?: string }> = ({
  data,
  className,
}) => {
  const payload = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const brochureUrl = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );
  const prevModuleIdx = useRef(0);

  const {
    section_background_image: bgImage = "",
    section_background_image_alt: bgAlt = "",
    section_overlay_rgba: overlayRgba = "",
    headline_primary = "The Complete Curriculum -",
    headline_primary_color = "#FFFFFF",
    headline_accent = "A Structured Path to Your Program",
    headline_accent_color = "#F5C542",
    topics_column_title = "Topic Covered",
    topics_inner_heading = "Topics",
    card_background_color = "#FFFFFF",
    module_list_track_background_color = "#E8EAED",
    content_panel_background_color = "#F3F4F6",
    module_inactive_border_color = "#E5E7EB",
    module_list_divider_color = "#94A3B8",
    module_active_background_color = "#FF6B00",
    module_active_text_color = "#FFFFFF",
    module_inactive_text_color = "#111827",
    section_background_color = "#0B0F14",
    cta_button_text = "Apply Now",
    cta_button_text_color = "#FFFFFF",
    cta_button_color = "#FF6B00",
    brochure_cta_text = "Download Brochure",
    brochure_cta_text_color = "#FFFFFF",
    brochure_cta_border_color = "#FFFFFF",
  } = payload;

  const payloadRaw = payload as Record<string, unknown>;
  const ctaButtonIsCta = isCmsShowFlagOn(
    payloadRaw?.cta_button_is_cta ?? payloadRaw?.ctaButtonIsCta,
    true,
  );
  const brochureCtaIsCta = isCmsShowFlagOn(
    payloadRaw?.brochure_cta_is_cta ?? payloadRaw?.brochureCtaIsCta,
    true,
  );

  const brochureTrim = (brochureUrl || "").trim();
  const brochureHref = brochureTrim ? normalizeHref(brochureTrim) : "";
  const primaryOpensLeadPopup = ctaButtonIsCta || !brochureHref;
  const brochureOpensLeadPopup = brochureCtaIsCta || !brochureHref;

  const modules = useMemo(() => {
    const rows = Array.isArray(payload.metadata) ? payload.metadata : [];
    return rows
      .filter(
        (r) =>
          r &&
          r.status !== false &&
          String(r.module_title ?? "").trim().length > 0,
      )
      .slice()
      .sort(
        (a, b) =>
          (Number(a.priority) || 0) - (Number(b.priority) || 0),
      );
  }, [payload.metadata]);

  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (selectedIdx >= modules.length) {
      setSelectedIdx(Math.max(0, modules.length - 1));
    }
  }, [modules.length, selectedIdx]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_structure_section");
  //   }
  // }, []);

  const active = modules[selectedIdx];

  const onSelectModule = useCallback(
    (idx: number) => {
      if (
        prevModuleIdx.current !== idx &&
        prevModuleIdx.current !== 0 &&
        modules[idx]
      ) {
        tabSwitched({
          section: "program_structure_section",
          component: "curriculum_module_list",
          label: modules[idx].module_title || `module_${idx}`,
        });
      }
      prevModuleIdx.current = idx;
      setSelectedIdx(idx);
    },
    [modules],
  );

  const onCta = useCallback(() => {
    leadCtaClick({
      section: "program_structure_section",
      component: "cta_button",
      label: cta_button_text,
    });
    // Apply Now is a plain lead capture — REGISTER keeps the brochure
    // auto-download disarmed. Only the brochure CTA below uses DOWNLOAD.
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch, cta_button_text]);

  const brochureLabel = (brochure_cta_text || "").trim();
  const onDownloadBrochure = useCallback(() => {
    if (!brochureLabel) return;
    leadCtaClick({
      section: "program_structure_section",
      component: "brochure_cta",
      label: brochureLabel,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochureUrl,
      }),
    );
  }, [brochureLabel, dispatch, brochureUrl]);

  if (payload.status === false) return null;

  if (modules.length === 0) return null;

  const bgSrc = (bgImage || "").trim();
  const overlayTrim = (overlayRgba || "").trim();

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative flex w-full items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden",
        className,
      )}
      style={{
        backgroundColor: section_background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {bgSrc ? (
        <>
          <Image
            src={bgSrc}
            alt={bgAlt || ""}
            fill
            className="object-cover object-center"
            sizes="100vw"
          />
          {overlayTrim ? (
            <div
              className="absolute inset-0 z-[1] pointer-events-none"
              style={{ backgroundColor: overlayTrim }}
              aria-hidden
            />
          ) : null}
        </>
      ) : null}

      <div
        className="relative z-10 w-full flex flex-col items-center justify-center min-[1280px]:w-screen"
        style={
          {
            "--ps3-active-bg": module_active_background_color,
            "--ps3-active-fg": module_active_text_color,
            "--ps3-inactive-fg": module_inactive_text_color,
          } as CSSProperties
        }
      >
        <div
          className={cn(
            "w-full flex flex-col items-center justify-center gap-7 min-[1024px]:gap-10",
            "pt-[30px] pb-20 min-[1024px]:py-[70px] px-[30px]",
          )}
        >
          <div className="flex flex-col items-center w-full max-w-[312px] min-[1024px]:max-w-none justify-center gap-[30px]">
            <h2 className="flex flex-col items-center justify-center text-center text-[32px] font-bold leading-[126%] text-white max-w-5xl">
              <span style={{ color: headline_primary_color }}>
                {headline_primary}
              </span>
              <span
                className="mt-1"
                style={{ color: headline_accent_color }}
              >
                {headline_accent}
              </span>
            </h2>
            <div className="flex min-[1024px]:flex-row flex-col gap-2 items-center justify-center min-[1024px]:gap-4 w-full min-[1024px]:w-auto">
              {primaryOpensLeadPopup ? (
              <Button
                type="button"
                onClick={onCta}
                className="rounded-md py-3 px-[18px] flex gap-2 items-center justify-center w-full min-[1024px]:w-auto h-auto text-base min-[1024px]:text-lg font-semibold"
                style={{
                  backgroundColor: cta_button_color,
                  color: cta_button_text_color,
                }}
              >
                {cta_button_text}
                <ArrowUpRight className="size-5 shrink-0" aria-hidden />
              </Button>
              ) : (
                <Link
                  href={brochureHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md py-3 px-[18px] flex gap-2 items-center justify-center w-full min-[1024px]:w-auto h-auto text-base min-[1024px]:text-lg font-semibold transition-opacity hover:opacity-95"
                  style={{
                    backgroundColor: cta_button_color,
                    color: cta_button_text_color,
                  }}
                  onClick={() =>
                    leadCtaClick({
                      section: "program_structure_section",
                      component: "cta_button_link",
                      label: cta_button_text,
                    })
                  }
                >
                  {cta_button_text}
                  <ArrowUpRight className="size-5 shrink-0" aria-hidden />
                </Link>
              )}
              {brochureLabel ? (
                brochureOpensLeadPopup ? (
                  <Button
                  type="button"
                  classType="ghost"
                  onClick={onDownloadBrochure}
                  className={cn(
                    "rounded-md border-2 border-solid bg-transparent py-3 px-[18px]",
                    "flex w-full items-center justify-center gap-2 min-[1024px]:w-auto",
                    "h-auto text-base font-semibold min-[1024px]:text-lg",
                    "transition-colors hover:bg-white/10",
                  )}
                  style={{
                    color: brochure_cta_text_color,
                    borderColor: brochure_cta_border_color,
                  }}
                >
                  {brochureLabel}
                  <ArrowDown
                    className="size-5 shrink-0 stroke-[2.25]"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  />
                </Button>
                ) : (
                  <Link
                    href={brochureHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "rounded-md border-2 border-solid bg-transparent py-3 px-[18px]",
                      "flex w-full items-center justify-center gap-2 min-[1024px]:w-auto",
                      "h-auto text-base font-semibold min-[1024px]:text-lg",
                      "transition-colors hover:bg-white/10",
                    )}
                    style={{
                      color: brochure_cta_text_color,
                      borderColor: brochure_cta_border_color,
                    }}
                    onClick={() =>
                      leadCtaClick({
                        section: "program_structure_section",
                        component: "brochure_cta_link",
                        label: brochureLabel,
                      })
                    }
                  >
                    {brochureLabel}
                    <ArrowDown
                      className="size-5 shrink-0 stroke-[2.25]"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    />
                  </Link>
                )
              ) : null}
            </div>
          </div>

          {/* Mobile: accordion */}
          <div className="min-[1024px]:hidden w-full flex flex-col gap-2">
            <Accordion
              type="single"
              collapsible
              defaultValue="0"
              className="w-full"
            >
              {modules.map((m, i) => (
                <AccordionItem
                  key={m.id ?? `m-${i}-${m.module_title}`}
                  value={String(i)}
                  className="border-0 border-b border-solid mb-0 pb-2 last:border-b-0 last:pb-0"
                  style={{ borderColor: module_list_divider_color }}
                >
                  <AccordionTrigger
                    adminIcon={false}
                    className={cn(
                      "flex-1 rounded-lg p-2 font-medium text-[18px] leading-[22px] border transition-colors",
                      "data-[state=open]:rounded-b-none data-[state=open]:rounded-t-lg",
                      "data-[state=open]:bg-[var(--ps3-active-bg)] data-[state=open]:text-[var(--ps3-active-fg)] data-[state=open]:border-transparent",
                      "data-[state=closed]:bg-white data-[state=closed]:text-[var(--ps3-inactive-fg)]",
                    )}
                    style={{ borderColor: module_inactive_border_color }}
                  >
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="text-left text-[14px] min-[1024px]:text-[18px] font-medium leading-[22px]">
                        {m.module_title}
                      </span>
                      <ChevronRight
                        className="size-6 shrink-0 rotate-90 transition-transform text-current"
                        aria-hidden
                      />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent
                    className="rounded-b-lg px-[3px] pb-2 bg-[var(--ps3-active-bg)] data-[state=open]:border data-[state=open]:border-transparent"
                  >
                    <div className="pt-2 pb-1 px-1">
                      <div
                        className="rounded-lg p-2"
                        style={{ backgroundColor: card_background_color }}
                      >
                        <TopicsBody
                          topicsColumnTitle={topics_column_title}
                          topicsInnerHeading={topics_inner_heading}
                          panelBg={content_panel_background_color}
                          topicDividerColor={module_inactive_border_color}
                          html={m.topics_html || ""}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Desktop: two columns */}
          <div
            className="hidden min-[1024px]:flex gap-4 min-[1280px]:gap-11 w-full max-w-6xl rounded-lg mx-2 min-[1024px]:px-2 min-[1024px]:py-6 min-[1024px]:pl-8 min-[1024px]:pr-12"
            style={{ backgroundColor: card_background_color }}
          >
            <div className="flex min-[1024px]:h-[500px] min-[1024px]:w-[min(440px,40vw)] min-[1280px]:w-[500px] flex-shrink-0 relative gap-2">
              <div
                className="h-full flex-1 overflow-y-auto min-w-0 pr-1 custom-scrollbar rounded-lg py-4 px-3 min-[1024px]:px-5"
                style={{ backgroundColor: module_list_track_background_color }}
              >
                <div className="flex flex-col gap-0 w-full min-w-0">
                  {modules.map((m, idx) => {
                    const isSel = idx === selectedIdx;
                    const showRule = idx < modules.length - 1;
                    return (
                      <div
                        key={m.id ?? `m-${idx}-${m.module_title}`}
                        className={cn(
                          "w-full min-w-0",
                          showRule && "border-b border-solid pb-4 mb-4 min-[1024px]:pb-5 min-[1024px]:mb-5",
                        )}
                        style={
                          showRule
                            ? { borderBottomColor: module_list_divider_color }
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          onClick={() => onSelectModule(idx)}
                          className={cn(
                            "w-full min-[1024px]:p-2 flex items-center justify-between gap-4 rounded-lg cursor-pointer text-left transition-colors",
                          )}
                          style={{
                            backgroundColor: isSel
                              ? module_active_background_color
                              : card_background_color,
                            color: isSel
                              ? module_active_text_color
                              : module_inactive_text_color,
                          }}
                        >
                          <p className="font-medium text-[16px] min-[1024px]:text-[18px] leading-[22px] min-w-0 flex-1">
                            {m.module_title}
                          </p>
                          <ChevronRight
                            className="size-6 shrink-0"
                            style={{
                              color: isSel
                                ? module_active_text_color
                                : module_inactive_text_color,
                            }}
                            aria-hidden
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 flex-1 min-w-0 min-[1024px]:w-[70%] justify-start">
              <TopicsBody
                topicsColumnTitle={topics_column_title}
                topicsInnerHeading={topics_inner_heading}
                panelBg={content_panel_background_color}
                topicDividerColor={module_inactive_border_color}
                html={active?.topics_html || ""}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramStructure3;
